import * as tf from "@tensorflow/tfjs";

const WEBGPU_VIDEO_TEXTURE_FLAG = "WEBGPU_IMPORT_EXTERNAL_TEXTURE";

/**
 * Apply ml5's default TensorFlow.js backend configuration.
 *
 * Works around a TensorFlow.js WebGPU bug where video frames imported through
 * `importExternalTexture` are read with the wrong orientation on iOS / WebKit.
 * This makes faceMesh, handPose, and bodyPose keypoints appear rotated and
 * misaligned with the video (ml5 issue #302). Disabling the flag makes the
 * WebGPU backend copy video frames instead, which keeps keypoints aligned while
 * preserving the WebGPU backend everywhere else.
 *
 * The flag is only set on detected iOS / iPadOS devices, so every other
 * platform keeps WebGPU's zero-copy `importExternalTexture` path and avoids
 * the per-frame copy cost. Safe to call more than once.
 *
 * Reported upstream as https://github.com/tensorflow/tfjs/issues/8733 — the
 * workaround can be removed once tf.js (or WebKit) fixes the import path.
 *
 * @return {void}
 */
export default function configureBackend() {
  // iPadOS reports itself as "MacIntel", hence the maxTouchPoints check.
  const isAppleTouchDevice =
    typeof navigator !== "undefined" &&
    (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

  if (!isAppleTouchDevice) return;

  try {
    tf.env().set(WEBGPU_VIDEO_TEXTURE_FLAG, false);
  } catch (e) {
    // The WebGPU backend (and therefore the flag) is not present in this
    // build, so there is nothing to work around.
    return;
  }

  // Only log where WebGPU can actually be active — elsewhere the flag change is
  // invisible and the notice is just noise.
  if (navigator.gpu) {
    console.info(
      "ml5.js: disabled WebGPU importExternalTexture so video keypoints stay aligned. " +
        "See https://github.com/ml5js/ml5-next-gen/issues/302"
    );
  }
}
